import Bugsnag from "@bugsnag/js";
import { ChatList, ChatMessages, deriveRoomName } from "@innerhour/chat-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
} from "use-query-params";

import axiosInstance from "../../axiosInstance";
import { removeNewChatUnreadRoomId } from "../../Store/Actions/chatActions";
import { updateClientChatRoomUnreadCount } from "../../Store/Actions/clientActions";
import { trackEvent, openLink } from "../../helperFunctions";
import { setCoupleNoteReportContext } from "../../utils/coupleNoteReportStorage";
import useMobileView from "../../hooks/useMobileView";
import { followupShortcuts } from "../../metadata/followupShortcuts";
import URL from "../../urls";
import { useChatStatus } from "../../hooks/useChatStatus";
import { useProviderChatStatus } from "../../hooks/useProviderChatStatus";
import { apiURL, CF_PROVIDER_ROLE, pwaURL } from "../../variable";
import FlexBox from "../common/FlexBox";
import FollowupModal from "./FollowupModal";
import ChatInfo from "./ChatInfo";

const ChatPanel = styled(FlexBox)`
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 100%;
  position: relative;
  flex-direction: column;
`;

const ChatMessagesOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  animation: slideInFromRight 280ms cubic-bezier(0.4, 0, 0.2, 1) forwards;

  @keyframes slideInFromRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const ChatNew = ({
  selectedRoomId,
  setSelectedRoomId,
  fromProfile,
  closeChat,
  cannedMessage,
  onCannedMessageConsumed,
  providerId,
  providerUuid,
  providerName,
  providerImage,
  toggleStatusModal,
}) => {
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [emailSource, setEmailSource] = useState("dropdown");
  const [activeRoom, setActiveRoom] = useState(null);
  const [sharedFilesOpen, setSharedFilesOpen] = useState(false);

  const authUser = useSelector(state => state.auth?.user);
  const isChatOpen = useSelector(state => state.chat.isChatOpen);
  const providerProfile = useSelector(state => state.provider?.providerProfile);
  const isMobile = useMobileView();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUserFBId = authUser?.firebaseid;
  const { chatStatus } = useChatStatus(authUserFBId);
  const { providerChatStatus, expiresAt: providerStatusExpiresAt } =
    useProviderChatStatus(authUserFBId);
  const [, setQueryParams] = useQueryParams({
    clientId: NumberParam,
    clientName: StringParam,
    client_uuid: StringParam,
    clientType: StringParam,
    firebaseId: StringParam,
    showNspModal: BooleanParam,
    showChatModal: BooleanParam,
    showChatList: BooleanParam,
    source: StringParam,
  });

  const client = activeRoom?.chat_participants?.find(
    p => p.role === "customer"
  );

  const isCouplesClient = activeRoom?.chat_type === "couple";

  const partner = isCouplesClient
    ? activeRoom?.chat_participants?.find(
        p => p.role === "customer" && p.user_id !== client?.user_id
      )
    : null;
  const showNsp = !!activeRoom?.show_nsp;

  const clientName = client?.user_name ?? "";

  const chatHeader = isCouplesClient ? deriveRoomName(activeRoom) : clientName;

  const {
    user_id: clientId,
    uuid: clientUuid,
    firebaseid: firebaseId,
  } = client ?? {};

  const handleSppExplore = payload => {
    const serviceTypeSlug =
      payload.serviceType === "couple_therapist"
        ? "couplestherapist"
        : payload.serviceType;
    openLink(`${pwaURL}/therapy-psychiatry/?activeTab=${serviceTypeSlug}`);
  };

  const fetchCommandsPage = useCallback(
    async ({ page, limit, customerUuid, signal }) => {
      const res = await axiosInstance.get(URL.getChatShortcuts, {
        params: {
          page,
          limit,
          ...(customerUuid ? { customer_uuid: customerUuid } : {}),
        },
        signal,
      });

      return {
        items: res?.data?.data || [],
        totalPages: res?.data?.total_page || 1,
      };
    },
    []
  );

  const searchCommands = useCallback(
    async ({ query, customerUuid, signal }) => {
      const res = await axiosInstance.get(URL.searchChatShortcut, {
        params: {
          query,
          ...(customerUuid ? { customer_uuid: customerUuid } : {}),
        },
        signal,
      });

      return res?.data?.data || [];
    },
    []
  );

  const fetchCommandById = useCallback(async ({ id, customerUuid, signal }) => {
    const res = await axiosInstance.get(`${URL.getChatShortcuts}/${id}`, {
      params: customerUuid ? { customer_uuid: customerUuid } : undefined,
      signal,
    });

    return res?.data?.data || null;
  }, []);

  useEffect(() => {
    setSharedFilesOpen(false);
  }, [selectedRoomId]);

  useEffect(() => {
    if (selectedRoomId) {
      const fetchRoomDetails = async () => {
        try {
          const response = await axiosInstance.get(
            `${apiURL}/chat/rooms/${selectedRoomId}`
          );

          setActiveRoom(response.data?.data ?? null);
        } catch (error) {
          Bugsnag.notify(error);
        }
      };
      fetchRoomDetails();
    }
  }, [selectedRoomId]);

  const menuItems = useMemo(() => {
    if (!client) return [];

    const {
      user_id: clientId,
      uuid: clientUuid,
      firebaseid: firebaseId,
      usertype: clientType,
      provider_removed_at,
      is_requested_user_care_coordinator,
    } = client;

    const isCareCoordinatorUser = !!is_requested_user_care_coordinator;

    const isCfProvider = providerProfile?.roles?.some(role =>
      CF_PROVIDER_ROLE.includes(role)
    );

    const trackMenuClick = ctaName => {
      trackEvent({
        event: "chat_kebab_item_click",
        payload: { provider_name: authUser?.name, cta: ctaName },
      });
    };
    const createNoteItem = ({ key, label, noteFor }) => {
      const coupleParam = noteFor ? `&noteFor=${noteFor}` : "";

      return {
        _key: key,
        label: label,
        onClick: async () => {
          if (
            noteFor === "couple" &&
            partner &&
            activeRoom?.client_partner_mapping_id
          ) {
            await setCoupleNoteReportContext({
              partnerId: partner.user_id,
              clientPartnerMappingId: activeRoom.client_partner_mapping_id,
              partnerName: partner.user_name,
            });
          }

          navigate(
            `/clients/${firebaseId}?client_uuid=${clientUuid}&showNotesModal=1&showNewNoteModal=1&clientId=${clientId}&source=chat${coupleParam}`
          );
          trackMenuClick("view note");
          trackEvent({
            event: "twa_client_new_note_cta",
            payload: { client_id: clientId, source: "chat" },
          });
        },
      };
    };

    const hasPartnerMapping = !!activeRoom?.client_partner_mapping_id;

    const allItems = [
      ...(isCouplesClient
        ? [
            createNoteItem({
              key: "between-note-individual",
              label: "Add new note for Individual",
            }),
            ...(hasPartnerMapping
              ? [
                  createNoteItem({
                    key: "between-note-couple",
                    label: "Add new note for Couple",
                    noteFor: "couple",
                  }),
                ]
              : []),
          ]
        : [createNoteItem({ key: "add_notes", label: "Add new notes" })]),
      {
        _key: "shared_files",
        label: "View shared files",
        onClick: () => {
          setSharedFilesOpen(true);
          trackMenuClick("shared files");
        },
      },
      {
        _key: "nsp",
        label: "Send next session prompt",
        onClick: () => {
          setQueryParams(
            {
              clientName,
              showNspModal: 1,
              clientId,
              clientType,
              client_uuid: clientUuid,
              ...(firebaseId && { firebaseId }),
              source: isCouplesClient
                ? "dashboard_group_chat"
                : "dashboard_individual_chat",
            },
            "replaceIn"
          );
          trackMenuClick("send session prompt");
        },
      },
      {
        _key: "follow_up_email",
        label: "Send follow-up email",
        onClick: () => {
          setEmailSource("dropdown");
          setShowFollowUpModal(true);
          trackMenuClick("send follow up mail");
        },
      },
    ];

    if (provider_removed_at) {
      return allItems
        .filter(item => item._key === "shared_files")
        .map(({ _key, ...item }) => item);
    }

    let filtered = allItems;
    if (isCouplesClient || isCareCoordinatorUser) {
      filtered = filtered.filter(item => item._key !== "follow_up_email");
    }
    if (isCareCoordinatorUser || !showNsp) {
      filtered = filtered.filter(item => item._key !== "nsp");
    }
    if (isCfProvider) {
      filtered = filtered.filter(item => item._key !== "follow_up_email");
    }
    if (!isCfProvider && isMobile) {
      filtered = filtered.filter(item => item._key !== "add_notes");
    }

    return filtered.map(({ _key, ...item }) => item);
  }, [
    client,
    partner,
    authUser,
    providerProfile,
    isMobile,
    navigate,
    setQueryParams,
    setSharedFilesOpen,
    isCouplesClient,
    showNsp,
    activeRoom,
    clientName,
  ]);

  const handleActionButtonClick = () => {
    setSelectedRoomId(null);
    if (fromProfile) closeChat();
  };

  const handleMarkReadSuccess = useCallback(
    roomId => {
      dispatch(removeNewChatUnreadRoomId(roomId));
      dispatch(updateClientChatRoomUnreadCount(roomId, 0));
    },
    [dispatch]
  );

  const handleFollowupShortcutSent = useCallback(() => {
    if (isCouplesClient || activeRoom?.follow_up_email_disabled) return;

    setEmailSource("shortcut");
    setShowFollowUpModal(true);
  }, [isCouplesClient, activeRoom]);

  const commandsConfig = useMemo(
    () => ({
      enabled: true,
      customerUuid: clientUuid,
      fetchPage: fetchCommandsPage,
      search: searchCommands,
      fetchById: fetchCommandById,
      pendingPrefill: cannedMessage?.id
        ? { id: cannedMessage.id, source: "canned-messages" }
        : null,
      onPendingPrefillConsumed: onCannedMessageConsumed,
      followupShortcutNames: followupShortcuts,
      onFollowupShortcutSent: handleFollowupShortcutSent,
    }),
    [
      clientUuid,
      fetchCommandsPage,
      searchCommands,
      fetchCommandById,
      cannedMessage,
      onCannedMessageConsumed,
      handleFollowupShortcutSent,
    ]
  );

  const clientProfileRoute = `/clients/${firebaseId}?client_uuid=${clientUuid}&client_id=${clientId}`;

  const followupChatClient = client
    ? {
        client: {
          info: {
            id: client.user_id,
            uuid: client.uuid,
          },
        },
      }
    : null;

  return (
    <ChatPanel>
      {showFollowUpModal && (
        <FollowupModal
          source={emailSource}
          toggleModal={() => setShowFollowUpModal(false)}
          chatClient={followupChatClient}
        />
      )}

      {!!selectedRoomId && (
        <ChatMessagesOverlay>
          <ChatMessages
            mode="provider"
            chatHeader={chatHeader}
            roomId={selectedRoomId}
            roomInfo={activeRoom}
            onActionButtonClick={handleActionButtonClick}
            onMarkReadSuccess={handleMarkReadSuccess}
            onRoomSelect={setSelectedRoomId}
            clientName={clientName}
            providerId={providerId}
            providerUuid={providerUuid}
            providerName={providerName}
            providerImage={providerImage}
            menuItems={menuItems}
            commands={commandsConfig}
            isCoupleRoom={isCouplesClient}
            onDocClick={openLink}
            systemMessageConfig={{
              spp: {
                variant: "provider",
                onExplore: handleSppExplore,
              },
            }}
            panels={{
              sharedFiles: {
                isOpen: sharedFilesOpen,
                onOpenChange: setSharedFilesOpen,
              },
            }}
            chatInfo={<ChatInfo />}
            {...(!fromProfile && {
              onNameClick: () => navigate(clientProfileRoute),
            })}
            isPartnerRemoved={activeRoom?.is_partner_removed}
            clientTermination={{
              isTerminated: !!client?.provider_removed_at,
              terminationDate: client?.provider_removed_at,
            }}
          />
        </ChatMessagesOverlay>
      )}

      {!fromProfile && (
        <ChatList
          isChatOpen={isChatOpen}
          activeRoomId={selectedRoomId}
          onRoomSelect={setSelectedRoomId}
          onStatusClick={toggleStatusModal}
          chatStatus={chatStatus}
          providerChatStatus={providerChatStatus}
          providerStatusExpiresAt={providerStatusExpiresAt}
          onClose={() => {
            if (isMobile) {
              setQueryParams(
                { showChatModal: null, showChatList: null },
                "replaceIn"
              );
            } else {
              dispatch({ type: "TOGGLE_CHAT_BLOCK" });
            }
          }}
        />
      )}
    </ChatPanel>
  );
};

export default ChatNew;
